'use client';

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { FiUsers, FiCreditCard, FiActivity, FiTrendingUp, FiFileText, FiAlertTriangle, FiCalendar } from 'react-icons/fi';
import { dashboardService, type DashboardStats, type RecentUser, type RecentPayment } from '@/lib/api/dashboard.service';

const DashboardPage = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [recentPayments, setRecentPayments] = useState<RecentPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResponse, usersResponse, paymentsResponse] = await Promise.all([
          dashboardService.getDashboardStats(),
          dashboardService.getRecentUsers(),
          dashboardService.getRecentPayments(),
        ]);

        if (statsResponse.success && statsResponse.data) {
          setStats(statsResponse.data);
        }
        if (usersResponse.success && usersResponse.data) {
          setRecentUsers(usersResponse.data);
        }
        if (paymentsResponse.success && paymentsResponse.data) {
          setRecentPayments(paymentsResponse.data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // 통계 카드 데이터 구성
  const statCards = stats ? [
    {
      title: '총 사용자',
      value: stats.totalUsers.value.toLocaleString(),
      change: `${stats.totalUsers.change}%`,
      changeType: stats.totalUsers.changeType,
      icon: FiUsers,
      color: 'blue',
    },
    {
      title: '이번 달 결제',
      value: `₩${stats.monthlyPayments.value.toLocaleString()}`,
      change: `${stats.monthlyPayments.change}%`,
      changeType: stats.monthlyPayments.changeType,
      icon: FiCreditCard,
      color: 'green',
    },
    {
      title: '위험도 평가',
      value: stats.totalRiskAssessments.value.toLocaleString(),
      change: `${stats.totalRiskAssessments.change}%`,
      changeType: stats.totalRiskAssessments.changeType,
      icon: FiActivity,
      color: 'orange',
    },
    {
      title: '월별 성장률',
      value: `${stats.monthlyGrowthRate.value}%`,
      change: `${stats.monthlyGrowthRate.change}%`,
      changeType: stats.monthlyGrowthRate.changeType,
      icon: FiTrendingUp,
      color: 'purple',
    },
    {
      title: '게시글 수',
      value: (stats.totalUsers.value * 0.3).toFixed(0), // 임시 데이터
      change: '12%',
      changeType: 'positive' as const,
      icon: FiFileText,
      color: 'indigo',
    },
    {
      title: '고위험 평가',
      value: Math.floor(stats.totalRiskAssessments.value * 0.15).toString(), // 임시 데이터
      change: '-5%',
      changeType: 'negative' as const,
      icon: FiAlertTriangle,
      color: 'red',
    },
    {
      title: '활성 구독',
      value: Math.floor(stats.totalUsers.value * 0.8).toString(), // 임시 데이터
      change: '12%',
      changeType: 'positive' as const,
      icon: FiCalendar,
      color: 'teal',
    },
  ] : [];

  const getStatCardClasses = (color: string) => {
    const colorMap = {
      blue:   'bg-blue-50   dark:bg-blue-900/30   text-blue-600   dark:text-blue-400',
      green:  'bg-green-50  dark:bg-green-900/30  text-green-600  dark:text-green-400',
      orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      red:    'bg-red-50    dark:bg-red-900/30    text-red-600    dark:text-red-400',
      teal:   'bg-teal-50   dark:bg-teal-900/30   text-teal-600   dark:text-teal-400',
    };
    return colorMap[color as keyof typeof colorMap] || 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">대시보드</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          SEEXR 관리자 시스템에 오신 것을 환영합니다.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2 whitespace-nowrap">
                      {stat.value}
                    </p>
                    <p className={`text-xs mt-2 whitespace-nowrap ${
                      stat.changeType === 'positive'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {stat.change} 지난 달 대비
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${getStatCardClasses(stat.color)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <Card>
          <CardHeader>
            <CardTitle>최근 가입 사용자</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentUsers.map((user, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <FiUsers className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user?.email || '-'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.timeAgo}
                      </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card>
          <CardHeader>
            <CardTitle>최근 결제</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentPayments.map((payment, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <FiCreditCard className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {payment.planName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {payment.timeAgo}
                        </p>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ₩{Number(payment.finalAmount).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>빠른 작업</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <FiUsers className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-medium text-gray-900 dark:text-gray-100">사용자 추가</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">새로운 사용자를 시스템에 추가</p>
            </button>

            <button className="p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <FiCreditCard className="w-8 h-8 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900 dark:text-gray-100">결제 내역</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">결제 내역 및 구독 관리</p>
            </button>

            <button className="p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <FiActivity className="w-8 h-8 text-orange-600 mb-2" />
              <h3 className="font-medium text-gray-900 dark:text-gray-100">위험도 분석</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">최근 위험도 평가 결과 확인</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
